import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/apiService';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';

interface UsageData {
  totalRequests: number;
  totalCreditsUsed: number;
  totalTokensUsed: number;
  apiKeysCount: number;
  activeKeysCount: number;
  dailyUsage: Record<string, { requests: number; credits: number; tokens: number }>;
  recentUsage: Array<{
    id: string;
    timestamp: Date;
    modelUsed: string;
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
    creditsCost: number;
    taskType: string;
    endpoint: string;
    apiKeyId: string;
    webSearchUsed: boolean;
    reasoningUsed: boolean;
    hasImage: boolean;
    hasDocument: boolean;
    processingTimeMs: number;
    ipAddress: string;
    taskDeterminationUsed: boolean;
  }>;
}

export default function Usage() {
  const [data, setData] = useState<UsageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeframe, setTimeframe] = useState<'7d' | '30d'>('30d');

  useEffect(() => {
    loadUsageData();
  }, []);

  const loadUsageData = async () => {
    try {
      const usageData = await apiService.getUsageAnalytics();
      setData(usageData);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatChartData = () => {
    if (!data?.dailyUsage) return [];
    
    const days = timeframe === '7d' ? 7 : 30;
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - days);

    const chartData = [];
    for (let i = 0; i < days; i++) {
      const date = new Date();
      date.setDate(endDate.getDate() - (days - 1 - i));
      const dateString = date.toISOString().split('T')[0];
      
      chartData.push({
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        requests: data.dailyUsage[dateString]?.requests || 0,
        credits: data.dailyUsage[dateString]?.credits || 0,
        tokens: data.dailyUsage[dateString]?.tokens || 0,
      });
    }
    return chartData;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
        Error: {error}
      </div>
    );
  }

  const chartData = formatChartData();

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Usage Analytics</h1>
          <p className="mt-1 text-sm text-gray-500">
            Track your API usage and spending patterns
          </p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => setTimeframe('7d')}
            className={`px-3 py-1 text-sm rounded ${
              timeframe === '7d' 
                ? 'bg-indigo-100 text-indigo-700' 
                : 'bg-gray-100 text-gray-700'
            }`}
          >
            7 days
          </button>
          <button
            onClick={() => setTimeframe('30d')}
            className={`px-3 py-1 text-sm rounded ${
              timeframe === '30d' 
                ? 'bg-indigo-100 text-indigo-700' 
                : 'bg-gray-100 text-gray-700'
            }`}
          >
            30 days
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Requests
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {data?.totalRequests?.toLocaleString() || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Credits Used
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    ${data?.totalCreditsUsed?.toFixed(4) || '0.0000'}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Tokens Used
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {data?.totalTokensUsed?.toLocaleString() || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Active API Keys
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {data?.activeKeysCount || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Avg Cost/Request
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    ${data?.totalRequests && data?.totalCreditsUsed 
                      ? (data.totalCreditsUsed / data.totalRequests).toFixed(6)
                      : '0.000000'
                    }
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Requests Chart */}
        <div className="bg-white p-6 shadow rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Daily Requests</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="requests" fill="#6366f1" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Credits Chart */}
        <div className="bg-white p-6 shadow rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Daily Credits Used</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip formatter={(value) => [`$${Number(value).toFixed(4)}`, 'Credits']} />
              <Line type="monotone" dataKey="credits" stroke="#10b981" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent Usage Table */}
      {data?.recentUsage && data.recentUsage.length > 0 && (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Recent API Calls
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Latest API usage activities (including free model calls)
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Model
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Input Tokens
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Output Tokens
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Tokens
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cost
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Features
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.recentUsage.slice(0, 10).map((usage, index) => (
                  <tr key={usage.id || index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(usage.timestamp).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span className="inline-flex px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                        {usage.modelUsed}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {usage.inputTokens.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {usage.outputTokens.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {usage.totalTokens.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {usage.creditsCost === 0 ? (
                        <span className="inline-flex px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                          Free
                        </span>
                      ) : (
                        `${usage.creditsCost.toFixed(6)}`
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex flex-wrap gap-1">
                        {usage.webSearchUsed && (
                          <span className="inline-flex px-1 py-0.5 text-xs bg-purple-100 text-purple-800 rounded">
                            Search
                          </span>
                        )}
                        {usage.reasoningUsed && (
                          <span className="inline-flex px-1 py-0.5 text-xs bg-orange-100 text-orange-800 rounded">
                            Reasoning
                          </span>
                        )}
                        {usage.hasImage && (
                          <span className="inline-flex px-1 py-0.5 text-xs bg-pink-100 text-pink-800 rounded">
                            Image
                          </span>
                        )}
                        {usage.hasDocument && (
                          <span className="inline-flex px-1 py-0.5 text-xs bg-cyan-100 text-cyan-800 rounded">
                            Doc
                          </span>
                        )}
                        {usage.taskDeterminationUsed && (
                          <span className="inline-flex px-1 py-0.5 text-xs bg-yellow-100 text-yellow-800 rounded">
                            Model Determination
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
